import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert, Button, CircularProgress, Container,
} from '@mui/material';
import { SmartToy as BotIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import CaptationChatWindow from './CaptationChatWindow';

/**
 * Página pública del bot de captación — entrada LIBRE (multi-vacante).
 * El bot ayuda a la persona a encontrar una vacante; al elegirla, la
 * conversación continúa como el flujo por vacante normal.
 *
 * Rutas: /chat (búsqueda global) · /chat/:slug (acotada a la organización)
 * En el subdominio del job board ({slug}.amandahr.ai/chat) se pasa hostSlug.
 */
const PublicFreeCaptationChat = ({ hostSlug = null }) => {
  const { t } = useTranslation();
  const { slug: paramSlug } = useParams();
  const slug = paramSlug || hostSlug || null;

  const storageKey = `captation-free-conversation-${slug || 'general'}`;

  const [conversationId, setConversationId] = useState(null);
  const [headerName, setHeaderName] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [finished, setFinished] = useState(false);
  const [expectsDocument, setExpectsDocument] = useState(false);

  const startConversation = useCallback(async () => {
    const response = await api.post('/captation-public/chat-libre/start', slug ? { slug } : {});
    const data = response.data.response;
    if (data?.status_response !== 'OK') throw new Error('start failed');

    sessionStorage.setItem(storageKey, data.conversationId);
    setConversationId(data.conversationId);
    setHeaderName(data.organizationName || '');
    setMessages([{ role: 'bot', content: data.botMessage }]);
    setFinished(false);
    setExpectsDocument(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const resumeOrStart = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const savedId = sessionStorage.getItem(storageKey);
      if (savedId) {
        try {
          const response = await api.get(`/captation-public/chat-libre/${savedId}`);
          const data = response.data.response;
          const conversation = data?.data;
          if (data?.status_response === 'OK' && conversation) {
            setConversationId(conversation.id);
            setHeaderName(conversation.positionName || conversation.organizationName || '');
            setMessages((conversation.transcript || []).map(({ role, content }) => ({ role, content })));
            setFinished(conversation.state === 'finalizada');
            setExpectsDocument(Boolean(conversation.expectsDocument));
            return;
          }
        } catch (resumeErr) {
          sessionStorage.removeItem(storageKey);
        }
      }

      await startConversation();
    } catch (err) {
      console.error(err);
      setError(t('ats.captation.public.error'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, startConversation]);

  useEffect(() => {
    resumeOrStart();
  }, [resumeOrStart]);

  const handleSend = async (text) => {
    setMessages((prev) => [...prev, { role: 'candidate', content: text }]);
    setSending(true);
    try {
      const response = await api.post(
        `/captation-public/chat-libre/${conversationId}/message`,
        { content: text }
      );
      const data = response.data.response;

      if (data?.status_response === 'OK') {
        setMessages((prev) => [...prev, { role: 'bot', content: data.botMessage }]);
        setExpectsDocument(Boolean(data.expectsDocument));
        if (data.positionName) setHeaderName(data.positionName);
        if (data.isCompleted || data.state === 'finalizada') {
          setFinished(true);
        }
      } else {
        setMessages((prev) => [...prev, { role: 'bot', content: t('ats.captation.public.error') }]);
      }
    } catch (err) {
      console.error(err);
      setMessages((prev) => [...prev, { role: 'bot', content: t('ats.captation.public.error') }]);
    } finally {
      setSending(false);
    }
  };

  const handleSendFile = async (file) => {
    setMessages((prev) => [...prev, { role: 'candidate', content: `📎 ${file.name}` }]);
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      const response = await api.post(
        `/captation-public/chat-libre/${conversationId}/document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const data = response.data.response;
      if (data?.status_response === 'OK') {
        setMessages((prev) => [...prev, { role: 'bot', content: data.botMessage }]);
        setExpectsDocument(Boolean(data.expectsDocument));
        if (data.isCompleted || data.state === 'finalizada') setFinished(true);
      } else {
        setMessages((prev) => [...prev, { role: 'bot', content: data?.message || t('ats.captation.public.uploadError') }]);
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.response?.message || t('ats.captation.public.uploadError');
      setMessages((prev) => [...prev, { role: 'bot', content: msg }]);
    } finally {
      setSending(false);
    }
  };

  // Alto exacto de la pantalla y sin scroll propio: solo hace scroll el área
  // de mensajes. 100dvh es el alto visible real en celular (descuenta la
  // barra del navegador); 100vh queda de respaldo en navegadores antiguos.
  return (
    <Box
      sx={{
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        overflow: 'hidden',
        bgcolor: 'grey.100',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Paper elevation={1} square sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <BotIcon color="primary" />
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            {headerName || t('ats.captation.publicFree.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('ats.captation.publicFree.subtitle')}
          </Typography>
        </Box>
      </Paper>

      <Container maxWidth="sm" sx={{ flex: 1, minHeight: 0, py: 2, display: 'flex', flexDirection: 'column' }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t('ats.captation.public.connecting')}
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={resumeOrStart}>
                {t('ats.captation.public.retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <CaptationChatWindow
              messages={messages}
              onSend={handleSend}
              sending={sending}
              finished={finished}
              expectsDocument={expectsDocument}
              onSendFile={handleSendFile}
              fill
            />
            {finished && (
              <Alert severity="success" sx={{ mt: 2, flexShrink: 0 }}>
                {t('ats.captation.public.finishedThanks')}
              </Alert>
            )}
          </>
        )}
      </Container>

      <Box sx={{ textAlign: 'center', pb: 1.5, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {t('ats.captation.public.poweredBy')}
        </Typography>
      </Box>
    </Box>
  );
};

export default PublicFreeCaptationChat;
